/**
 * Constructor Facets Component
 *
 * Drop-in replacement for the legacy Searchspring facet element.
 * Keeps the same custom element tag (`constructor-facets`) and CSS classes
 * so existing styling/layout continue to work.
 */

import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import {
  escapeHtml,
  getColorSwatchStyle,
  isColorFacetField,
  PICKUP_FACET_FIELD,
} from '@theme/constructor-utils';

/**
 * @typedef {object} ConstructorFacetValue
 * @property {string} value
 * @property {string} [display_name]
 * @property {string} [label]
 * @property {number} [count]
 * @property {boolean} [selected]
 * @property {{ selected?: boolean }} [status]
 */

/**
 * @typedef {object} ConstructorFacet
 * @property {string} [name]
 * @property {string} [field]
 * @property {string} [display_name]
 * @property {string} [label]
 * @property {ConstructorFacetValue[]} [data]
 * @property {ConstructorFacetValue[]} [options]
 * @property {ConstructorFacetValue[]} [values]
 */

/**
 * @typedef {object} FacetValue
 * @property {string} value
 * @property {string} label
 * @property {number} count
 * @property {boolean} active
 */

/**
 * @typedef {object} FacetSplitSection
 * @property {string} label
 * @property {FacetValue[]} values
 */

/**
 * @typedef {object} Facet
 * @property {string} field
 * @property {string} label
 * @property {FacetValue[]} values
 * @property {string} [splitLabel] - Heading for the split-size section (e.g. "Jeans size").
 * @property {FacetValue[]} [splitValues] - Split-size values rendered under the split heading.
 * @property {FacetSplitSection[]} [splitSections] - Multiple split-size sections rendered below the primary size grid.
 */

const CHECKBOX_SELECTOR = '.cnstrc-facet__input';
const FILTER_PARAM_PREFIX = 'filter.';
const FILTER_PARAM_PREFIX_LENGTH = FILTER_PARAM_PREFIX.length;
const PAGE_PARAM = 'page';
const DESKTOP_DRAWER_QUERY = '(min-width: 1080px)';
const FILTER_CHANGE_DEBOUNCE_MS = 180;

/** Field whose values can contain both base and split size groups. */
const SIZE_FIELD = 'size';
/** Marker that distinguishes a clothing size (contains "/") from a jeans size. */
const CLOTHING_SIZE_MARKER = '/';
/** Marker used by Dickey closing sizes (e.g. "30-31"). */
const DICKEY_SIZE_MARKER = '-';
/** Hidden marker facet used to signal jeans-size priority handling. */
const JEANS_SIZING_FIELD = 'jeans_sizing';
/** Size prefixes that move from clothing section into jeans when marker is present. */
const JEANS_PRIORITY_SIZE_PREFIXES = ['24'];

/** Alpha size order for sorting letter sizes */
const ALPHA_SIZE_ORDER = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '2XL',
  'XXXL',
  '3XL',
  '4XL',
  'ONE SIZE',
  'OS',
  'OSFM',
];
/** Regex to detect letter-size values */
const ALPHA_SIZE_PATTERN = /^(XXS|XS|S|M|L|XL|XXL|XXXL|2XL|3XL|4XL|ONE SIZE|OS|OSFM)$/i;

class ConstructorFacetsComponent extends Component {
  #controller = new AbortController();
  /** @type {Facet[] | null} */
  #lastFacets = null;
  /** @type {string} */
  #lastRenderSignature = '';
  /** @type {string} */
  #lastHiddenFacetFieldsRaw = '';
  /** @type {Set<string>} */
  #lastHiddenFacetFields = new Set();
  /** @type {string} */
  #lastReplacementValuesRaw = '';
  /** @type {Map<string, Array<{ value: string, label: string }>>} */
  #lastReplacementValuesByField = new Map();

  /**
   * Normalize refs that can be a single element or an array.
   * @template T
   * @param {T | T[] | undefined} ref
   * @returns {T | undefined}
   */
  #getRefElement(ref) {
    return Array.isArray(ref) ? ref[0] : ref;
  }

  get drawerMode() {
    return this.hasAttribute('data-drawer') || this.dataset.drawer === 'true';
  }

  get drawerDesktopMode() {
    return (
      this.drawerMode &&
      typeof window !== 'undefined' &&
      window.matchMedia(DESKTOP_DRAWER_QUERY).matches
    );
  }

  /**
   * Locale string for the jeans-size split section label (e.g. "Jeans size").
   * Sourced from data-jeans-size-split-label in Liquid.
   * Falls back to legacy data-size-split-label.
   * @returns {string}
   */
  get #jeansSizeSplitLabel() {
    return this.dataset.jeansSizeSplitLabel || this.dataset.sizeSplitLabel || '';
  }

  /**
   * Locale string for the Dickey-size split section label (e.g. "Dickey size").
   * Sourced from data-dickey-size-split-label in Liquid.
   * @returns {string}
   */
  get #dickeySizeSplitLabel() {
    return this.dataset.dickeySizeSplitLabel || '';
  }

  /** Set of facet fields to hide, passed through data-facets-hide. @returns {Set<string>} */
  get #hiddenFacetFields() {
    const raw = this.dataset.facetsHide ?? '[]';
    if (raw === this.#lastHiddenFacetFieldsRaw) return this.#lastHiddenFacetFields;

    /** @type {Set<string>} */
    let hiddenFields = new Set();
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        hiddenFields = new Set(
          parsed.filter((value) => typeof value === 'string' && value.length > 0),
        );
      }
    } catch {
      // Ignore invalid theme setting JSON and fall back to no hidden fields.
    }

    this.#lastHiddenFacetFieldsRaw = raw;
    this.#lastHiddenFacetFields = hiddenFields;
    return hiddenFields;
  }

  /**
   * Parse custom replacement values for featured filters.
   * @returns {Map<string, Array<{ value: string, label: string }>>}
   */
  get #replacementValuesByField() {
    const raw = this.dataset.facetsReplacement ?? '[]';
    if (raw === this.#lastReplacementValuesRaw) return this.#lastReplacementValuesByField;

    /** @type {Map<string, Array<{ value: string, label: string }>>} */
    const replacements = new Map();
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (!item?.field || item?.value == null) continue;

          const field = String(item.field);
          const value = String(item.value);
          const label = String(item.label ?? item.value);
          const values = replacements.get(field);
          if (values) {
            values.push({ value, label });
          } else {
            replacements.set(field, [{ value, label }]);
          }
        }
      }
    } catch {
      // Ignore invalid theme setting JSON and fall back to no replacement values.
    }

    this.#lastReplacementValuesRaw = raw;
    this.#lastReplacementValuesByField = replacements;
    return replacements;
  }

  /**
   * Build selected facet values once from URL for the current render cycle.
   * @returns {Map<string, Set<string>>}
   */
  #getSelectedValuesByField() {
    /** @type {Map<string, Set<string>>} */
    const selectedValuesByField = new Map();
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params) {
      if (!key.startsWith(FILTER_PARAM_PREFIX)) continue;
      const field = key.slice(FILTER_PARAM_PREFIX_LENGTH);
      const selectedValues = selectedValuesByField.get(field);
      if (selectedValues) {
        selectedValues.add(value);
      } else {
        selectedValuesByField.set(field, new Set([value]));
      }
    }
    return selectedValuesByField;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('cnstrc:facets-update', this.#handleFacetsUpdate, { signal });
    this.addEventListener('change', this.#handleFilterChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });

    if (this.drawerMode) {
      const mq = window.matchMedia(DESKTOP_DRAWER_QUERY);
      mq.addEventListener(
        'change',
        () => {
          if (this.#lastFacets) this.#renderFacets(this.#lastFacets);
        },
        { signal },
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * @param {Event} event
   */
  #handleFacetsUpdate = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const rawConstructorFacets = event.detail?.constructorFacets;
    const normalizedFacets = event.detail?.facets;

    // Prefer the normalized payload emitted by results-list to avoid
    // re-normalizing large facet payloads on every response.
    const nextFacets = Array.isArray(normalizedFacets)
      ? this.#normalizeExistingFacets(normalizedFacets)
      : Array.isArray(rawConstructorFacets)
        ? this.#normalizeConstructorFacets(rawConstructorFacets)
        : [];

    this.#renderFacets(nextFacets);
  };

  /**
   * @param {ConstructorFacet[]} facets
   * @returns {Facet[]}
   */
  #normalizeConstructorFacets(facets) {
    const urlParams = new URLSearchParams(window.location.search);
    /** @type {Facet[]} */
    const normalized = [];

    for (const facet of facets) {
      const field = String(facet?.name || facet?.field || '').trim();
      if (!field) continue;

      const label = String(facet?.display_name || facet?.label || field).trim();
      const rawValues = Array.isArray(facet?.data)
        ? facet.data
        : Array.isArray(facet?.options)
          ? facet.options
          : Array.isArray(facet?.values)
            ? facet.values
            : [];

      const selectedValues = new Set(urlParams.getAll(`${FILTER_PARAM_PREFIX}${field}`));
      /** @type {FacetValue[]} */
      const values = [];

      for (const value of rawValues) {
        const rawValue = value?.value == null ? '' : String(value.value);
        if (!rawValue) continue;

        const active =
          Boolean(value?.selected || value?.status?.selected) || selectedValues.has(rawValue);
        const count = Number(value?.count ?? 0);

        values.push({
          value: rawValue,
          label: String(value?.display_name || value?.label || rawValue),
          count: Number.isFinite(count) ? count : 0,
          active,
        });
      }

      normalized.push({
        field,
        label,
        values,
      });
    }

    return normalized;
  }

  /**
   * Supports already-normalized facets to remain backwards compatible.
   * @param {Array<{ field?: string, label?: string, display_name?: string, values?: any[] }>} facets
   * @returns {Facet[]}
   */
  #normalizeExistingFacets(facets) {
    /** @type {Facet[]} */
    const normalized = [];

    for (const facet of facets) {
      const field = String(facet?.field || '').trim();
      if (!field) continue;

      const label = String(facet?.label || facet?.display_name || field);
      /** @type {FacetValue[]} */
      const values = [];

      if (Array.isArray(facet?.values)) {
        for (const value of facet.values) {
          const rawValue = value?.value == null ? '' : String(value.value);
          if (!rawValue) continue;
          const count = Number(value?.count ?? 0);
          values.push({
            value: rawValue,
            label: String(value?.label || value?.display_name || rawValue),
            count: Number.isFinite(count) ? count : 0,
            active: Boolean(value?.active || value?.selected || value?.status?.selected),
          });
        }
      }

      normalized.push({ field, label, values });
    }

    return normalized;
  }

  /**
   * Split the `size` facet into clothing sizes + split size sections.
   *
   * "/" means clothing (primary grid), "-" means Dickey, the rest are jeans.
   * With clothing values present, jeans and Dickey render as separate split
   * sections. Without them, a lone split section is promoted to the main facet
   * label; if both split groups exist, both render as sections.
   *
   * @param {Facet[]} facets
   * @param {boolean} hasJeansSizing
   * @returns {Facet[]}
   */
  #applySizeSplits(facets, hasJeansSizing) {
    return facets.map((facet) => {
      if (facet.field !== SIZE_FIELD) return facet;

      const clothingSourceValues = facet.values.filter((value) =>
        value.label.includes(CLOTHING_SIZE_MARKER),
      );
      /** @type {FacetValue[]} */
      const prioritizedJeansValues = [];
      /** @type {FacetValue[]} */
      const clothingValues = [];
      for (const value of clothingSourceValues) {
        const slashIndex = value.label.indexOf(CLOTHING_SIZE_MARKER);
        const labelPrefix = slashIndex >= 0 ? value.label.slice(0, slashIndex).trim() : '';
        const valuePrefix =
          slashIndex < 0 && value.value.includes(CLOTHING_SIZE_MARKER)
            ? value.value.slice(0, value.value.indexOf(CLOTHING_SIZE_MARKER)).trim()
            : '';
        const sizePrefix = labelPrefix || valuePrefix;
        const shouldPrioritizeAsJeans =
          hasJeansSizing &&
          sizePrefix.length > 0 &&
          JEANS_PRIORITY_SIZE_PREFIXES.includes(sizePrefix);

        if (shouldPrioritizeAsJeans) {
          prioritizedJeansValues.push({ ...value, label: sizePrefix });
        } else {
          clothingValues.push(value);
        }
      }
      const dickeyValues = this.#sortSizeValues(
        facet.values.filter(
          (value) =>
            value.label.includes(DICKEY_SIZE_MARKER) && !value.label.includes(CLOTHING_SIZE_MARKER),
        ),
      );
      const jeansBaseValues = facet.values.filter(
        (value) =>
          !value.label.includes(CLOTHING_SIZE_MARKER) && !value.label.includes(DICKEY_SIZE_MARKER),
      );
      const sortedClothingValues = this.#sortSizeValues(clothingValues);
      const jeansValues = this.#sortSizeValues([...jeansBaseValues, ...prioritizedJeansValues]);

      /** @type {FacetSplitSection[]} */
      const splitSections = [];
      if (jeansValues.length > 0 && this.#jeansSizeSplitLabel) {
        splitSections.push({ label: this.#jeansSizeSplitLabel, values: jeansValues });
      }
      if (dickeyValues.length > 0 && this.#dickeySizeSplitLabel) {
        splitSections.push({ label: this.#dickeySizeSplitLabel, values: dickeyValues });
      }

      if (sortedClothingValues.length > 0) {
        return {
          ...facet,
          values: sortedClothingValues,
          splitSections,
          // Legacy props retained for compatibility with existing code paths.
          splitLabel: splitSections[0]?.label,
          splitValues: splitSections[0]?.values,
        };
      }

      const [firstSection] = splitSections;
      if (!firstSection) return facet;

      if (splitSections.length === 1) {
        return {
          ...facet,
          label: firstSection.label,
          values: firstSection.values,
          splitSections: undefined,
          splitLabel: undefined,
          splitValues: undefined,
        };
      }

      // No clothing values but both split groups exist: render both sections.
      return {
        ...facet,
        values: [],
        splitSections,
        splitLabel: splitSections[0]?.label,
        splitValues: splitSections[0]?.values,
      };
    });
  }

  /**
   * Order size values the way Searchspring does: letter sizes first (in
   * {@link ALPHA_SIZE_ORDER} order), followed by remaining values sorted
   * numerically ascending. Returns a new array; the input is not mutated.
   *
   * @param {FacetValue[]} values
   * @returns {FacetValue[]}
   */
  #sortSizeValues(values) {
    const alphaSizes = values.filter((value) => ALPHA_SIZE_PATTERN.test(value.value.trim()));
    const numericSizes = values.filter((value) => !ALPHA_SIZE_PATTERN.test(value.value.trim()));

    alphaSizes.sort((a, b) => {
      const aIndex = ALPHA_SIZE_ORDER.findIndex(
        (size) => size.toLowerCase() === a.value.toLowerCase(),
      );
      const bIndex = ALPHA_SIZE_ORDER.findIndex(
        (size) => size.toLowerCase() === b.value.toLowerCase(),
      );
      return aIndex - bIndex;
    });
    numericSizes.sort((a, b) => parseFloat(a.value) - parseFloat(b.value));

    return [...alphaSizes, ...numericSizes];
  }

  /**
   * @param {Facet[]} facets
   */
  #renderFacets(facets) {
    const containerEl = this.#getRefElement(this.refs.facetsContainer);
    if (!(containerEl instanceof HTMLElement)) return;

    const hasJeansSizing = facets.some((facet) => facet.field === JEANS_SIZING_FIELD);
    const replacementValuesByField = this.#replacementValuesByField;
    const hiddenFacetFields = this.#hiddenFacetFields;
    const selectedValuesByField = this.#getSelectedValuesByField();
    /** @type {Set<string>} */
    const facetFields = new Set();
    /** @type {Facet[]} */
    const visibleFacets = [];

    for (const facet of facets) {
      facetFields.add(facet.field);
      if (facet.field === JEANS_SIZING_FIELD || facet.field === PICKUP_FACET_FIELD) continue;
      if (hiddenFacetFields.has(facet.field)) continue;

      let hasVisibleValue = false;
      for (const value of facet.values) {
        if (value.active || value.count > 0) {
          hasVisibleValue = true;
          break;
        }
      }
      if (!hasVisibleValue) continue;

      visibleFacets.push(
        this.#mergeReplacementValues(facet, replacementValuesByField, selectedValuesByField),
      );
    }

    replacementValuesByField.forEach((replacementValues, field) => {
      if (hiddenFacetFields.has(field) || facetFields.has(field)) return;
      visibleFacets.push({
        field,
        label: field.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
        values: replacementValues.map((value) => ({
          value: value.value,
          label: value.label,
          count: 0,
          active: this.#isValueSelected(field, value.value, selectedValuesByField),
        })),
      });
    });

    const layoutMode = this.drawerMode
      ? this.drawerDesktopMode
        ? 'drawer-desktop'
        : 'drawer-mobile'
      : 'inline';
    const renderSignature = `${layoutMode}::${hasJeansSizing ? 'jeans' : 'default'}::${JSON.stringify(
      visibleFacets.map((facet) => ({
        field: facet.field,
        values: facet.values.map((value) => [value.value, value.count, value.active]),
      })),
    )}`;

    if (renderSignature === this.#lastRenderSignature) {
      this.#updateClearAllVisibility();
      return;
    }

    this.#lastFacets = facets;
    this.#lastRenderSignature = renderSignature;

    const finalFacets = this.#applySizeSplits(visibleFacets, hasJeansSizing);
    containerEl.innerHTML = finalFacets.map((facet) => this.#renderFacet(facet)).join('');
    this.#updateClearAllVisibility();
  }

  /**
   * @param {Facet} facet
   * @param {Map<string, Array<{ value: string, label: string }>>} replacementValuesByField
   * @param {Map<string, Set<string>>} [selectedValuesByField]
   * @returns {Facet}
   */
  #mergeReplacementValues(facet, replacementValuesByField, selectedValuesByField) {
    const replacementValues = replacementValuesByField.get(facet.field);
    if (!replacementValues?.length) return facet;

    const valueByRawValue = new Map(facet.values.map((value) => [value.value, value]));
    const mergedValues = replacementValues.map((replacement) => {
      const matched = valueByRawValue.get(replacement.value);
      return {
        value: replacement.value,
        label: replacement.label,
        count: matched?.count ?? 0,
        active:
          matched?.active ??
          this.#isValueSelected(facet.field, replacement.value, selectedValuesByField),
      };
    });

    return {
      ...facet,
      values: mergedValues,
    };
  }

  /**
   * @param {string} field
   * @param {string} value
   * @param {Map<string, Set<string>>} [selectedValuesByField]
   */
  #isValueSelected(field, value, selectedValuesByField) {
    if (selectedValuesByField) {
      return selectedValuesByField.get(field)?.has(value) === true;
    }
    return new URLSearchParams(window.location.search)
      .getAll(`${FILTER_PARAM_PREFIX}${field}`)
      .includes(value);
  }

  /**
   * @param {Facet} facet
   */
  #renderFacet(facet) {
    let activeCount = 0;
    for (const value of facet.values) {
      if (value.active) activeCount += 1;
    }

    const splitSections =
      facet.splitSections ??
      (facet.splitLabel && facet.splitValues
        ? [{ label: facet.splitLabel, values: facet.splitValues }]
        : []);

    // Split-size values count towards the facet's active total in headings.
    for (const section of splitSections) {
      for (const value of section.values) {
        if (value.active) activeCount += 1;
      }
    }

    const itemsMarkup = facet.values
      .map((value) => this.#renderFacetValue(facet.field, facet.label, value))
      .join('');

    const splitMarkup = splitSections
      .map((section) => this.#renderSizeSplitSection(facet.field, section))
      .join('');

    const contentMarkup = `
      <div class="cnstrc-facet__content">
        ${
          itemsMarkup
            ? this.#renderVisualFieldset({
                legend: facet.label,
                itemsMarkup,
                legendClass: 'visually-hidden',
              })
            : ''
        }
        ${splitMarkup}
      </div>
    `;

    if (this.drawerMode) {
      if (this.drawerDesktopMode) {
        return `
          <div class="cnstrc-facet cnstrc-facet--drawer-desktop" data-facet-field="${escapeHtml(facet.field)}">
            <h3 class="cnstrc-facet__label-heading">${escapeHtml(facet.label)}${activeCount > 0 ? ` (${activeCount})` : ''}</h3>
            ${contentMarkup}
          </div>
        `;
      }

      return `
        <accordion-custom class="cnstrc-facet-accordion accordion--plus" data-facet-field="${escapeHtml(facet.field)}">
          <details class="details cnstrc-facet__panel">
            <summary class="details__header facets__summary">
              <span class="cnstrc-facet__label">${escapeHtml(facet.label)}${activeCount > 0 ? ` <span>(${activeCount})</span>` : ''}</span>
              <span class="cnstrc-facet__accordion-icon">
                <span class="svg-wrapper icon-plus" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 0V14" stroke="black"/>
                    <path d="M14 7L7.2227e-06 7" stroke="black"/>
                  </svg>
                </span>
                <span class="svg-wrapper icon-minus" aria-hidden="true">
                  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.28 7.14001L2.77828e-05 7.14001" stroke="black"/>
                  </svg>
                </span>
              </span>
            </summary>
            <div class="details-content">${contentMarkup}</div>
          </details>
        </accordion-custom>
      `;
    }

    return `
      <div class="cnstrc-facet" data-facet-field="${escapeHtml(facet.field)}">
        <details class="cnstrc-facet__panel" open>
          <summary class="cnstrc-facet__summary">
            <span class="cnstrc-facet__label">${escapeHtml(facet.label)}</span>
            ${activeCount > 0 ? `<span>(${activeCount})</span>` : ''}
          </summary>
          ${contentMarkup}
        </details>
      </div>
    `;
  }

  /**
   * Render the split-size section appended below the primary size grid.
   * @param {string} field
   * @param {FacetSplitSection} section
   * @returns {string}
   */
  #renderSizeSplitSection(field, section) {
    const splitValues = section.values ?? [];
    let splitActiveCount = 0;
    for (const value of splitValues) {
      if (value.active) splitActiveCount += 1;
    }

    const splitItemsMarkup = splitValues
      .map((value) => this.#renderFacetValue(field, section.label, value))
      .join('');

    const splitLegend = `${section.label}${splitActiveCount > 0 ? ` (${splitActiveCount})` : ''}`;

    return `
      <div class="cnstrc-facet__size-split">
        ${this.#renderVisualFieldset({
          legend: splitLegend,
          itemsMarkup: splitItemsMarkup,
          legendClass: 'cnstrc-facet__label-heading label-size-split',
        })}
      </div>
    `;
  }

  /**
   * Render a grouped set of facet checkboxes with a semantic legend.
   * @param {{ legend: string, itemsMarkup: string, legendClass?: string }} config
   * @returns {string}
   */
  #renderVisualFieldset(config) {
    const legendClass = config.legendClass ? ` class="${escapeHtml(config.legendClass)}"` : '';

    return `
      <fieldset class="cnstrc-facet__fieldset">
        <legend${legendClass}>${escapeHtml(config.legend)}</legend>
        <div class="cnstrc-facet__visual-grid">
          ${config.itemsMarkup}
        </div>
      </fieldset>
    `;
  }

  /**
   * @param {string} field
   * @param {string} facetLabel
   * @param {FacetValue} value
   */
  #renderFacetValue(field, facetLabel, value) {
    const shouldRenderSwatch = isColorFacetField(field);

    return `
      <label class="cnstrc-facet__visual-item ${value.active ? 'cnstrc-facet__visual-item--active' : ''} ${value.count === 0 ? 'cnstrc-facet__visual-item--disabled' : ''}">
        <input
          type="checkbox"
          name="filter.${escapeHtml(field)}"
          value="${escapeHtml(value.value)}"
          ${value.active ? 'checked' : ''}
          ${value.count === 0 && !value.active ? 'disabled' : ''}
          class="cnstrc-facet__input visually-hidden"
        />
        ${shouldRenderSwatch ? `<span class="cnstrc-facet__swatch" style="${getColorSwatchStyle(value, this.dataset.colorSwatchPrefix)}"></span>` : ''}
        <span class="cnstrc-facet__visual-label">${escapeHtml(value.label)}</span>
        <span class="cnstrc-facet__visual-count">(${value.count})</span>
        ${
          value.active
            ? `
          <button
            type="button"
            class="cnstrc-facet__visual-item-remove"
            aria-label="Remove ${escapeHtml(facetLabel)}: ${escapeHtml(value.label)}"
            data-cnstrc-remove-visual
          >
            <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `
            : ''
        }
      </label>
    `;
  }

  #handleFilterChange = debounce((event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches(CHECKBOX_SELECTOR)) return;

    this.#updateFilters();
  }, FILTER_CHANGE_DEBOUNCE_MS);

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const removeButton = target.closest('[data-cnstrc-remove-visual]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      const item = removeButton.closest('.cnstrc-facet__visual-item');
      const input = item?.querySelector(CHECKBOX_SELECTOR);
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (target.closest('[data-cnstrc-clear-all]')) {
      this.#clearAllFilters();
    }
  };

  #updateFilters() {
    /** @type {HTMLInputElement[]} */
    const checkedInputs = [];
    for (const input of this.querySelectorAll(`${CHECKBOX_SELECTOR}:checked`)) {
      if (input instanceof HTMLInputElement) checkedInputs.push(input);
    }

    const url = new URL(window.location.href);
    this.#clearManagedFilterParams(url);

    for (const input of checkedInputs) {
      url.searchParams.append(input.name, input.value);
    }

    url.searchParams.delete(PAGE_PARAM);
    history.pushState({}, '', url.toString());

    performance.mark('facet-filter-dispatched');
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.FilterUpdate, {
        detail: {
          filters: checkedInputs.map((input) => `${input.name}=${encodeURIComponent(input.value)}`),
        },
      }),
    );
  }

  #clearAllFilters() {
    const url = new URL(window.location.href);
    this.#clearAllFilterParams(url);
    url.searchParams.delete(PAGE_PARAM);
    history.pushState({}, '', url.toString());

    performance.mark('facet-filter-dispatched');
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.FilterUpdate, {
        detail: { filters: [] },
      }),
    );
  }

  /**
   * @param {URL} url
   */
  #clearAllFilterParams(url) {
    for (const key of new Set(url.searchParams.keys())) {
      if (key.startsWith(FILTER_PARAM_PREFIX)) url.searchParams.delete(key);
    }
  }

  /**
   * Filter param names this component renders as checkboxes (e.g. "filter.size").
   * Used to scope URL rebuilds so filters owned elsewhere (e.g. featured filters)
   * are not destroyed when this component applies its own filters.
   * @returns {Set<string>}
   */
  #getManagedFilterParamNames() {
    /** @type {Set<string>} */
    const names = new Set();
    for (const input of this.querySelectorAll(CHECKBOX_SELECTOR)) {
      if (input instanceof HTMLInputElement && input.name.startsWith(FILTER_PARAM_PREFIX)) {
        names.add(input.name);
      }
    }
    return names;
  }

  /**
   * Clear only the filter params this component manages, preserving any others.
   * @param {URL} url
   */
  #clearManagedFilterParams(url) {
    const managed = this.#getManagedFilterParamNames();
    for (const key of new Set(url.searchParams.keys())) {
      if (managed.has(key)) url.searchParams.delete(key);
    }
  }

  #updateClearAllVisibility() {
    const clearAllButtonEl = this.#getRefElement(this.refs.clearAllButton);
    if (!(clearAllButtonEl instanceof HTMLElement)) return;

    const hasActiveFilters = this.querySelector(`${CHECKBOX_SELECTOR}:checked`) != null;
    clearAllButtonEl.toggleAttribute('hidden', !hasActiveFilters);
  }
}

if (!customElements.get('constructor-facets')) {
  customElements.define('constructor-facets', ConstructorFacetsComponent);
}

export { ConstructorFacetsComponent };
